import { useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { cn } from "@/cn";
import { getDocumentSendDownloadUrl } from "@/api/documents";
import {
  useCreateDocumentSend,
  useDocumentSends,
  useDocuments,
} from "@/hooks/useDocuments";
import type { DocumentSendChannel } from "@career-ops-amplify/shared";

const CHANNELS: { value: DocumentSendChannel; label: string }[] = [
  { value: "apply", label: "Apply" },
  { value: "email", label: "Email" },
  { value: "portal", label: "Portal" },
  { value: "other", label: "Other" },
];

export function JobDocumentSendsPanel({
  jobId,
  companyId,
}: {
  jobId: string;
  companyId?: string;
}) {
  const { data: documents = [] } = useDocuments();
  const { data: sends = [], isLoading } = useDocumentSends(jobId);
  const createSend = useCreateDocumentSend();
  const [selected, setSelected] = useState<string[]>([]);
  const [channel, setChannel] = useState<DocumentSendChannel>("portal");
  const [notes, setNotes] = useState("");
  const [error, setError] = useState<string | null>(null);
  const [downloading, setDownloading] = useState(false);

  const activeDocs = useMemo(
    () => documents.filter((d) => d.isActive !== false),
    [documents],
  );

  function toggle(id: string) {
    setSelected((prev) =>
      prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id],
    );
  }

  async function handleMarkSent() {
    setError(null);
    if (selected.length === 0) {
      setError("Select at least one document");
      return;
    }
    try {
      await createSend.mutateAsync({
        jobId,
        companyId,
        channel,
        documentIds: selected,
        notes: notes.trim() || undefined,
      });
      setSelected([]);
      setNotes("");
    } catch (err) {
      setError(err instanceof Error ? err.message : "Failed to record send");
    }
  }

  async function downloadSentFile(sendId: string, index: number) {
    setDownloading(true);
    try {
      const { url, fileName } = await getDocumentSendDownloadUrl(sendId, index);
      const a = document.createElement("a");
      a.href = url;
      a.download = fileName;
      a.target = "_blank";
      a.rel = "noopener noreferrer";
      a.click();
    } catch (err) {
      setError(err instanceof Error ? err.message : "Download failed");
    } finally {
      setDownloading(false);
    }
  }

  return (
    <section className="rounded-lg border border-border bg-card p-5">
      <h3 className="mb-1 text-xs font-semibold uppercase tracking-wide text-muted-foreground">
        Documents sent
      </h3>
      <p className="mb-4 text-xs text-muted-foreground">
        Track what you submitted for this role. Sent copies are frozen in S3.
      </p>

      <div className="mb-4 space-y-2">
        {activeDocs.length === 0 ? (
          <p className="text-xs text-muted-foreground">
            Upload documents in{" "}
            <Link to="/assets" className="text-brand hover:underline">
              Assets
            </Link>{" "}
            first.
          </p>
        ) : (
          activeDocs.map((doc) => (
            <label
              key={doc.id}
              className="flex cursor-pointer items-center gap-2 text-sm"
            >
              <input
                type="checkbox"
                checked={selected.includes(doc.id)}
                onChange={() => toggle(doc.id)}
                className="rounded border-border"
              />
              <span className="truncate">
                {doc.originalFileName ?? doc.fileName}
              </span>
              <span className="text-[10px] uppercase text-muted-foreground">
                {doc.docType}
              </span>
            </label>
          ))
        )}
      </div>

      <div className="mb-3 flex flex-wrap gap-2">
        <select
          value={channel}
          onChange={(e) => setChannel(e.target.value as DocumentSendChannel)}
          className="rounded-md border border-border bg-bg px-2 py-1.5 text-xs"
        >
          {CHANNELS.map((c) => (
            <option key={c.value} value={c.value}>
              {c.label}
            </option>
          ))}
        </select>
        <input
          type="text"
          value={notes}
          onChange={(e) => setNotes(e.target.value)}
          placeholder="Notes (optional)"
          className="min-w-[12rem] flex-1 rounded-md border border-border bg-bg px-2 py-1.5 text-xs"
        />
        <button
          type="button"
          onClick={() => void handleMarkSent()}
          disabled={createSend.isPending || selected.length === 0}
          className={cn(
            "rounded-md bg-brand px-3 py-1.5 text-xs font-medium text-brand-foreground",
            "hover:opacity-90 disabled:cursor-not-allowed disabled:opacity-50",
          )}
        >
          {createSend.isPending ? "Saving…" : "Mark as sent"}
        </button>
      </div>

      {error && <p className="mb-3 text-xs text-destructive">{error}</p>}

      <div className="border-t border-border pt-3">
        <h4 className="mb-2 text-[11px] font-medium uppercase tracking-wide text-muted-foreground">
          History
        </h4>
        {isLoading && (
          <p className="text-xs text-muted-foreground">Loading…</p>
        )}
        {!isLoading && sends.length === 0 && (
          <p className="text-xs text-muted-foreground">No sends recorded yet.</p>
        )}
        <ul className="space-y-2">
          {sends.map((send) => (
            <li
              key={send.id}
              className="rounded-md border border-border/60 bg-bg/40 px-3 py-2 text-xs"
            >
              <div className="flex flex-wrap items-center justify-between gap-2">
                <span className="font-medium text-foreground">
                  {new Date(send.sentAt).toLocaleString()} · {send.channel}
                </span>
                <span className="text-muted-foreground">
                  {send.s3Keys.length} file
                  {send.s3Keys.length === 1 ? "" : "s"}
                </span>
              </div>
              {send.notes && (
                <p className="mt-1 text-muted-foreground">{send.notes}</p>
              )}
              <div className="mt-2 flex flex-wrap gap-1">
                {send.s3Keys.map((key, index) => (
                  <button
                    key={key}
                    type="button"
                    disabled={downloading}
                    className="rounded border border-border px-2 py-0.5 text-[11px] hover:bg-muted/20 disabled:opacity-50"
                    onClick={() => void downloadSentFile(send.id, index)}
                  >
                    Download {key.split("/").pop()}
                  </button>
                ))}
              </div>
            </li>
          ))}
        </ul>
      </div>
    </section>
  );
}
